import RequestTextInput, { StableLabel } from "./RequestTextInput";
import { isOverRequestTextLimit, REQUEST_TEXT_TOO_LONG_MESSAGE, shouldAnalyse } from "./requestText";

/** Lucide-style outline icon: 20px, stroke 1.5, currentColor (see the gd-icon class). */
function Glyph({ children }) {
  return (
    <svg className="gd-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

function SearchGlyph() {
  return (
    <Glyph>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </Glyph>
  );
}

export function FileGlyph() {
  return (
    <Glyph>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    </Glyph>
  );
}

function UploadGlyph() {
  return (
    <Glyph>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" x2="12" y1="3" y2="15" />
    </Glyph>
  );
}

function CloseGlyph() {
  return (
    <Glyph>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Glyph>
  );
}

function AlertGlyph() {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </Glyph>
  );
}

/** Formats a byte count as a human-readable KB/MB string, e.g. "240 KB" or "1.3 MB".
 * Duplicated from App.jsx/BriefView.jsx (not exported from either) — small
 * enough not to be worth a shared module for now. */
function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

const exampleQueries = [
  "payment screen",
  "agent dashboard",
  "onboarding flow",
  "empty state",
  "B2C mobile",
];

// Hints for the search box, longest first; the box shows the first one that fits
// on one line (see useFittingHint). The first is the preferred wording.
const SEARCH_HINTS = [
  "Search a screen, describe a need, or paste a brief, email or ticket...",
  "Search, describe a need, or paste an email or ticket...",
  "Search, describe a need, or paste text...",
  "Search or paste text...",
];

const SEARCH_LABEL = "Search";
const ANALYSE_LABEL = "Find references";
const ANALYSING_LABEL = "Reading your text\u2026";
const LANDING_PRIMARY_LABELS = [SEARCH_LABEL, ANALYSE_LABEL];
const RESULTS_PRIMARY_LABELS = [SEARCH_LABEL, ANALYSE_LABEL, ANALYSING_LABEL];

/** Attach button shown inside the search field, at its inline end, in the results view. */
function AttachButton({ onOpenFilePicker, isLoading, buttonRef }) {
  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={onOpenFilePicker}
      disabled={isLoading}
      aria-label="Upload a requirements document"
      title="Upload a requirements document"
      className="gd-btn gd-btn--ghost gd-btn--icon gd-search__action"
    >
      <UploadGlyph />
    </button>
  );
}

/** The request text field with its search icon (and, in the results view, the attach button). */
function QueryField({ query, onQueryChange, submit, overLimit, isLoading, autoFocus, attach }) {
  return (
    <div className="gd-search">
      <span className="gd-search__icon" aria-hidden="true">
        <SearchGlyph />
      </span>
      <RequestTextInput
        autoFocus={autoFocus}
        value={query}
        onChange={onQueryChange}
        onSubmit={submit}
        hints={SEARCH_HINTS}
        className="gd-search__input app-request-input"
        aria-label="Search, describe a need, or paste text"
        aria-describedby={overLimit ? "request-text-limit-message" : undefined}
        aria-invalid={overLimit}
        disabled={isLoading}
        aria-busy={isLoading}
      />
      {attach ? (
        <AttachButton onOpenFilePicker={attach.onOpenFilePicker} isLoading={isLoading} buttonRef={attach.buttonRef} />
      ) : null}
    </div>
  );
}

/** Prominent empty-state dropzone shown below the search field on the landing page.
 * The whole box is clickable/keyboard-activatable to open the file picker. */
function UploadDropzone({ onOpenFilePicker, isLoading, dragActive, dropzoneRef }) {
  return (
    <div
      className="gd-empty app-dropzone"
      ref={dropzoneRef}
      role="button"
      tabIndex={isLoading ? -1 : 0}
      aria-label="Upload a requirements document"
      aria-describedby="upload-guidance"
      aria-disabled={isLoading}
      data-active={!isLoading && dragActive}
      onClick={() => { if (!isLoading) onOpenFilePicker(); }}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !isLoading) {
          e.preventDefault();
          onOpenFilePicker();
        }
      }}
    >
      <div className="gd-empty__mark app-mark" aria-hidden="true">
        <UploadGlyph />
      </div>
      <h3 className="gd-empty__title">Drag and drop your requirements document</h3>
      <p id="upload-guidance" className="gd-empty__text">
        or <span className="app-dropzone__browse">browse files</span> · .txt or .pdf · up to 10 MB
      </p>
    </div>
  );
}

function SearchLoadingStatus({ isLoading, isAnalysing }) {
  if (!isLoading) return null;
  return (
    <span role="status" aria-live="polite" aria-atomic="true" className="app-visually-hidden">
      {isAnalysing ? ANALYSING_LABEL : "Searching..."}
    </span>
  );
}

/** The visible words for a running plain search (the primary button already says it while analysing). */
function SearchProgressText({ isLoading, isAnalysing }) {
  if (!isLoading || isAnalysing) return null;
  return (
    <span className="small gd-muted app-search-status" aria-hidden="true">
      Searching&hellip;
    </span>
  );
}

function ErrorNotice({ children }) {
  return (
    <div className="gd-notice gd-notice--danger" role="alert">
      <span className="gd-notice__icon">
        <AlertGlyph />
      </span>
      <div className="gd-notice__body">{children}</div>
    </div>
  );
}

/** Filled-state card shown below the search field once a file is attached, replacing the dropzone. */
function AttachedFileCard({
  attachedFiles,
  onRemoveFile,
  isLoading,
  reportPurpose,
  reportPurposeInputRef,
  onReportPurposeChange,
  onGenerateReport,
}) {
  const handlePurposeKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!isLoading) onGenerateReport();
  };

  return (
    <div className="gd-card gd-stack">
      <div className="gd-stack">
        {attachedFiles.map((file, index) => (
          <div key={`${index}-${file.name}`} className="app-file-row">
            <span className="app-file-icon" aria-hidden="true">
              <FileGlyph />
            </span>
            <div className="app-file-row__text">
              <div className="body-strong app-file-row__name">{file.name}</div>
              <p className="small gd-muted app-file-row__meta">
                {formatFileSize(file.size)} · ready to generate a report
              </p>
            </div>
            <button
              type="button"
              onClick={() => onRemoveFile(index)}
              aria-label={`Remove ${file.name}`}
              className="gd-btn gd-btn--ghost gd-btn--icon"
            >
              <CloseGlyph />
            </button>
          </div>
        ))}
      </div>

      <div className="app-attached__section">
        <h3 className="heading app-attached__title">Generate a report from this document</h3>
        <div className="app-attached__form">
          <div className="gd-field">
            <label htmlFor="report-purpose" className="gd-label">
              Purpose (optional)
            </label>
            <input
              id="report-purpose"
              type="text"
              ref={reportPurposeInputRef}
              value={reportPurpose || ""}
              onChange={(e) => onReportPurposeChange(e.target.value)}
              onKeyDown={handlePurposeKeyDown}
              placeholder="What should this report help with?"
              disabled={isLoading}
              aria-busy={isLoading}
              className="gd-input"
            />
          </div>
          <button type="button" onClick={onGenerateReport} disabled={isLoading} className="gd-btn">
            Generate report
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SearchBox({
  variant = "empty",
  query,
  onQueryChange,
  reportPurpose,
  reportPurposeInputRef,
  uploadDropzoneRef,
  filePickerRef,
  onReportPurposeChange,
  onGenerateReport,
  onSubmit,
  onNewSearch,
  onExampleChip,
  isLoading,
  isAnalysing = false,
  attachedFiles,
  onOpenFilePicker,
  onRemoveFile,
  attachError,
  dragActive,
}) {
  const hasAttachedFiles = (attachedFiles || []).length > 0;
  const overLimit = isOverRequestTextLimit(query);
  const primaryDisabled = isLoading || overLimit || !(query || "").trim();
  // The label says which path the Enter key / button will take before it is pressed.
  // While any plain search runs (including the fallback after an analysis) it reads "Search", as plain search always has.
  const primaryLabel = isAnalysing
    ? ANALYSING_LABEL
    : !isLoading && shouldAnalyse(query)
      ? ANALYSE_LABEL
      : SEARCH_LABEL;

  const submit = () => {
    if (primaryDisabled) return;
    onSubmit();
  };

  const clearInput = () => {
    onQueryChange("");
  };

  const limitMessage = overLimit ? (
    <p id="request-text-limit-message" role="alert" className="gd-error">
      {REQUEST_TEXT_TOO_LONG_MESSAGE}
    </p>
  ) : null;

  const attachedCard = hasAttachedFiles ? (
    <AttachedFileCard
      attachedFiles={attachedFiles}
      onRemoveFile={onRemoveFile}
      isLoading={isLoading}
      reportPurpose={reportPurpose}
      reportPurposeInputRef={reportPurposeInputRef}
      onReportPurposeChange={onReportPurposeChange}
      onGenerateReport={onGenerateReport}
    />
  ) : null;

  const attachErrorNotice = attachError ? <ErrorNotice>{attachError}</ErrorNotice> : null;

  if (variant === "results") {
    return (
      <div className="gd-stack">
        <SearchLoadingStatus isLoading={isLoading} isAnalysing={isAnalysing} />
        <div className="gd-search-row">
          <QueryField
            query={query}
            onQueryChange={onQueryChange}
            submit={submit}
            overLimit={overLimit}
            isLoading={isLoading}
            attach={{ onOpenFilePicker, buttonRef: filePickerRef }}
          />
          <button
            type="button"
            onClick={clearInput}
            disabled={isLoading || !query}
            className="gd-btn gd-btn--ghost"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={primaryDisabled}
            className="gd-btn gd-btn--primary"
          >
            <StableLabel labels={RESULTS_PRIMARY_LABELS} active={primaryLabel} />
          </button>
          <button type="button" onClick={onNewSearch} disabled={isLoading} className="gd-btn">
            New search
          </button>
          <SearchProgressText isLoading={isLoading} isAnalysing={isAnalysing} />
        </div>

        {limitMessage}
        {attachedCard}
        {attachErrorNotice}
      </div>
    );
  }

  return (
    <div className="gd-stack gd-stack--loose">
      <SearchLoadingStatus isLoading={isLoading} isAnalysing={isAnalysing} />

      <div className="app-hero">
        <h2 className="display app-hero__title">Find the screen you already designed.</h2>
        <p className="body gd-muted app-hero__lead">
          Search over <strong className="body-strong">100,000</strong> screens from Amdocs design projects in Figma.
        </p>
      </div>

      <div className="gd-stack">
        <div className="gd-search-row">
          <QueryField
            autoFocus
            query={query}
            onQueryChange={onQueryChange}
            submit={submit}
            overLimit={overLimit}
            isLoading={isLoading}
          />
          <button type="button" onClick={submit} disabled={primaryDisabled} className="gd-btn gd-btn--primary">
            <StableLabel labels={LANDING_PRIMARY_LABELS} active={primaryLabel} />
          </button>
          <SearchProgressText isLoading={isLoading} isAnalysing={isAnalysing} />
        </div>

        {limitMessage}

        <div className="gd-row gd-row--wrap">
          <span className="small gd-muted">Try</span>
          {exampleQueries.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => onExampleChip(chip)}
              disabled={isLoading}
              className="gd-chip app-chip"
            >
              {chip}
            </button>
          ))}
        </div>
      </div>

      <div className="app-divider">
        <span className="small gd-muted">
          {hasAttachedFiles ? "or generate a report from this document" : "or upload a requirements document"}
        </span>
      </div>

      {hasAttachedFiles ? (
        attachedCard
      ) : (
        <UploadDropzone
          onOpenFilePicker={onOpenFilePicker}
          isLoading={isLoading}
          dragActive={dragActive}
          dropzoneRef={uploadDropzoneRef}
        />
      )}

      {attachErrorNotice}
    </div>
  );
}
